// Artwork node detail: lineage timeline (concept → prompt → original →
// derivatives → rendered preview → production), preview on light/dark, node
// metadata, and the owner actions — review, normalize, upload an edited
// derivative, render a text preview, and promote to a production asset.

import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api.js';
import type {
  ArtworkAsset,
  PersonalizationDef,
  PrintQaRecord,
  ProductTemplate,
  RenderQaReport,
  TextLayout,
} from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

function errMsg(err: unknown): string {
  return err instanceof ApiClientError ? err.message : String(err);
}

export function ArtworkDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['artwork'] });

  const nodeQuery = useQuery({
    queryKey: ['artwork', id],
    queryFn: () => api.get<ArtworkAsset>(`/artwork/${id}`),
  });
  const node = nodeQuery.data?.data;

  const lineageQuery = useQuery({
    queryKey: ['artwork', 'lineage', node?.lineageId],
    queryFn: () => api.get<ArtworkAsset[]>(`/artwork/lineage/${node!.lineageId}`),
    enabled: !!node?.lineageId,
  });
  const lineage = lineageQuery.data?.data ?? [];

  if (nodeQuery.isLoading) return <p className="muted">Loading…</p>;
  if (!node) return <p className="form-error">Artwork not found.</p>;

  return (
    <section>
      <p>
        <Link to="/artwork">← Artwork</Link>
      </p>
      <h1>
        {node.role} <StatusBadge status={node.status} />
      </h1>

      <div className="split">
        <div className="card">
          <h2>Preview</h2>
          <div className="preview-swatches">
            <div className="preview-light">
              <img src={`/api/v1/artwork/${node.id}/content`} alt="artwork on light" />
            </div>
            <div className="preview-dark">
              <img src={`/api/v1/artwork/${node.id}/content`} alt="artwork on dark" />
            </div>
          </div>
          <dl className="meta">
            <dt>Dimensions</dt>
            <dd>
              {node.file.width}×{node.file.height}
            </dd>
            <dt>SHA-256</dt>
            <dd className="mono">{node.file.sha256.slice(0, 16)}…</dd>
            <dt>Transparency</dt>
            <dd>
              {node.analysis.hasTransparentPixels ? 'real alpha' : 'none'}
              {node.analysis.checkerboardDetected ? ' · checkerboard detected' : ''}
            </dd>
            <dt>Filename</dt>
            <dd>{node.provenance.originalFilename}</dd>
          </dl>
          {node.externalGeneration && (
            <dl className="meta">
              <dt>Provider</dt>
              <dd>{node.externalGeneration.provider}</dd>
              <dt>Model</dt>
              <dd>{node.externalGeneration.model ?? 'unknown'}</dd>
              <dt>Method</dt>
              <dd>{node.externalGeneration.method}</dd>
              <dt>Prompt hash</dt>
              <dd className="mono">
                {node.externalGeneration.promptHash
                  ? `${node.externalGeneration.promptHash.slice(0, 16)}…`
                  : '—'}
              </dd>
              {node.externalGeneration.uncertaintyNote && (
                <>
                  <dt>Uncertainty</dt>
                  <dd>{node.externalGeneration.uncertaintyNote}</dd>
                </>
              )}
            </dl>
          )}
        </div>

        <div className="card">
          <h2>Lineage timeline</h2>
          <ol className="timeline">
            {lineage.map((n) => (
              <li
                key={n.id}
                className={n.id === node.id ? 'timeline-item current' : 'timeline-item'}
              >
                <Link to={`/artwork/${n.id}`}>
                  <strong>{n.role}</strong>
                </Link>{' '}
                <StatusBadge status={n.status} />
                <div className="muted">
                  {n.derivationType}
                  {n.transformation.type === 'render'
                    ? ` · layout v${String(n.transformation.layoutVersion ?? '')}`
                    : ''}
                  {n.transformation.type === 'production'
                    ? ` · template v${String(n.transformation.templateVersion ?? '')}`
                    : ''}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {actionError && (
        <p role="alert" className="form-error">
          {actionError}
        </p>
      )}

      <ReviewPanel node={node} onDone={invalidate} onError={setActionError} />
      <NormalizePanel node={node} onDone={invalidate} onError={setActionError} />
      <EditedPanel node={node} onDone={invalidate} onError={setActionError} />
      <RenderPanel node={node} onDone={invalidate} onError={setActionError} />
      <PromotePanel node={node} onDone={invalidate} onError={setActionError} />
    </section>
  );
}

function ReviewPanel({
  node,
  onDone,
  onError,
}: {
  node: ArtworkAsset;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [notes, setNotes] = useState('');
  const review = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      api.post(`/artwork/${node.id}/review`, { decision, notes: notes || undefined }),
    onSuccess: onDone,
    onError: (e) => onError(errMsg(e)),
  });
  if (node.status !== 'pending') return null;
  return (
    <div className="card">
      <h2>Design review</h2>
      <p className="muted">Approve or reject this variant. Transparency approval is separate.</p>
      <input
        type="text"
        placeholder="Notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="button-row">
        <button type="button" onClick={() => review.mutate('approved')} disabled={review.isPending}>
          Approve
        </button>
        <button
          type="button"
          className="danger"
          onClick={() => review.mutate('rejected')}
          disabled={review.isPending}
        >
          Reject
        </button>
      </div>
    </div>
  );
}

function NormalizePanel({
  node,
  onDone,
  onError,
}: {
  node: ArtworkAsset;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [method, setMethod] = useState('checkerboard');
  const [tolerance, setTolerance] = useState(30);
  const normalize = useMutation({
    mutationFn: () => api.post(`/artwork/${node.id}/normalize`, { method, tolerance }),
    onSuccess: onDone,
    onError: (e) => onError(errMsg(e)),
  });
  if (node.role !== 'original' && node.role !== 'externally_edited') return null;
  return (
    <div className="card">
      <h2>Normalize background</h2>
      <p className="muted">Creates a normalized derivative without touching this node.</p>
      <div className="form-row">
        <label htmlFor="nm-method">Method</label>
        <select id="nm-method" value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="checkerboard">Checkerboard → alpha</option>
          <option value="solid_edge">Solid edge flood-fill</option>
          <option value="both">Both</option>
        </select>
      </div>
      <div className="form-row">
        <label htmlFor="nm-tol">Tolerance</label>
        <input
          id="nm-tol"
          type="number"
          min={0}
          max={255}
          value={tolerance}
          onChange={(e) => setTolerance(Number(e.target.value))}
        />
      </div>
      <button type="button" onClick={() => normalize.mutate()} disabled={normalize.isPending}>
        {normalize.isPending ? 'Normalizing…' : 'Create normalized derivative'}
      </button>
    </div>
  );
}

function EditedPanel({
  node,
  onDone,
  onError,
}: {
  node: ArtworkAsset;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [editTool, setEditTool] = useState('');
  const [editor, setEditor] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const edited = useMutation({
    mutationFn: () => {
      const file = fileRef.current?.files?.[0];
      if (!file) throw new Error('Select a PNG file');
      const form = new FormData();
      form.append('file', file);
      form.append('originalFilename', file.name);
      form.append('editTool', editTool);
      form.append('editor', editor);
      form.append('editSummary', editSummary);
      return api.upload(`/artwork/${node.id}/edited`, form);
    },
    onSuccess: onDone,
    onError: (e) => onError(errMsg(e)),
  });
  if (node.role === 'rendered_preview' || node.role === 'production') return null;
  return (
    <div className="card">
      <h2>Upload edited derivative</h2>
      <p className="muted">Record an externally edited PNG as a child of this node.</p>
      <div className="form-row">
        <label htmlFor="ed-file">PNG file</label>
        <input id="ed-file" type="file" accept="image/png" ref={fileRef} />
      </div>
      <div className="form-row">
        <label htmlFor="ed-tool">Edit tool</label>
        <input id="ed-tool" value={editTool} onChange={(e) => setEditTool(e.target.value)} />
      </div>
      <div className="form-row">
        <label htmlFor="ed-editor">Editor</label>
        <input id="ed-editor" value={editor} onChange={(e) => setEditor(e.target.value)} />
      </div>
      <div className="form-row">
        <label htmlFor="ed-summary">Edit summary</label>
        <input
          id="ed-summary"
          value={editSummary}
          onChange={(e) => setEditSummary(e.target.value)}
        />
      </div>
      <button type="button" onClick={() => edited.mutate()} disabled={edited.isPending}>
        {edited.isPending ? 'Uploading…' : 'Upload edited derivative'}
      </button>
    </div>
  );
}

function RenderPanel({
  node,
  onDone,
  onError,
}: {
  node: ArtworkAsset;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [layoutId, setLayoutId] = useState('');
  const [useExample, setUseExample] = useState(true);
  const [background, setBackground] = useState('#ffffff');
  const [values, setValues] = useState<Record<string, string>>({});
  const [qa, setQa] = useState<RenderQaReport | null>(null);

  const layoutsQuery = useQuery({
    queryKey: ['text-layouts', node.conceptId],
    queryFn: () => api.get<TextLayout[]>(`/text-layouts?conceptId=${node.conceptId}&pageSize=100`),
    enabled: !!node.conceptId,
  });
  const layouts = layoutsQuery.data?.data ?? [];
  const layout = layouts.find((l) => l.id === layoutId);

  const defQuery = useQuery({
    queryKey: ['personalization-def', layout?.personalizationDefId],
    queryFn: () =>
      api.get<PersonalizationDef>(`/personalization-defs/${layout!.personalizationDefId}`),
    enabled: !!layout?.personalizationDefId && !useExample,
  });
  const fields = defQuery.data?.data.fields ?? [];

  const render = useMutation({
    mutationFn: () =>
      api.post<ArtworkAsset>(`/text-layouts/${layoutId}/render`, {
        baseAssetId: node.id,
        useExampleValues: useExample,
        values: useExample ? undefined : values,
        backgroundColor: background,
      }),
    onSuccess: async (res) => {
      setQa((res.meta as { qa?: RenderQaReport } | undefined)?.qa ?? null);
      onDone();
    },
    onError: (e) => onError(errMsg(e)),
  });

  // Only base-like nodes can be rendered onto.
  if (node.role === 'rendered_preview' || node.role === 'production') return null;

  return (
    <div className="card">
      <h2>Render text preview</h2>
      <p className="muted">Composites a personalization layout onto this artwork.</p>
      <div className="form-row">
        <label htmlFor="rn-layout">Text layout</label>
        <select id="rn-layout" value={layoutId} onChange={(e) => setLayoutId(e.target.value)}>
          <option value="">Select a layout…</option>
          {layouts.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} (v{l.version}, {l.status})
            </option>
          ))}
        </select>
      </div>
      <div className="form-row">
        <label htmlFor="rn-bg">Blank color</label>
        <input
          id="rn-bg"
          type="color"
          value={background}
          onChange={(e) => setBackground(e.target.value)}
        />
      </div>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={useExample}
          onChange={(e) => setUseExample(e.target.checked)}
        />
        Use sample values
      </label>
      {!useExample &&
        fields.map((f) => (
          <div className="form-row" key={f.fieldKey}>
            <label htmlFor={`rn-${f.fieldKey}`}>{f.label}</label>
            <input
              id={`rn-${f.fieldKey}`}
              maxLength={f.maxLength}
              placeholder={f.placeholder}
              value={values[f.fieldKey] ?? ''}
              onChange={(e) => setValues((v) => ({ ...v, [f.fieldKey]: e.target.value }))}
            />
          </div>
        ))}
      <button
        type="button"
        onClick={() => render.mutate()}
        disabled={render.isPending || !layoutId}
      >
        {render.isPending ? 'Rendering…' : 'Render preview'}
      </button>
      {qa && <RenderQaView qa={qa} />}
    </div>
  );
}

function RenderQaView({ qa }: { qa: RenderQaReport }) {
  return (
    <div className={qa.ok ? 'qa qa-ok' : 'qa qa-bad'}>
      <strong>QA {qa.ok ? 'passed' : 'has issues'}</strong>
      <table className="qa-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Rendered</th>
            <th>Fits</th>
            <th>Font</th>
            <th>Contrast</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {qa.fields.map((f) => (
            <tr key={f.fieldKey}>
              <td>{f.fieldKey}</td>
              <td>{f.rendered ? 'yes' : 'hidden'}</td>
              <td>{f.fits ? 'ok' : 'overflow'}</td>
              <td>{f.appliedFontSize}px</td>
              <td>{f.contrastPass == null ? '—' : f.contrastPass ? 'ok' : 'low'}</td>
              <td>{f.errors.join('; ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PromotePanel({
  node,
  onDone,
  onError,
}: {
  node: ArtworkAsset;
  onDone: () => void;
  onError: (m: string) => void;
}) {
  const [templateId, setTemplateId] = useState('');
  const [decision, setDecision] = useState<'pass' | 'fail'>('pass');
  const [notes, setNotes] = useState('');
  const [qa, setQa] = useState<PrintQaRecord | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['product-templates', node.conceptId],
    queryFn: () =>
      api.get<ProductTemplate[]>(`/product-templates?conceptId=${node.conceptId}&pageSize=100`),
    enabled: !!node.conceptId,
  });
  const templates = templatesQuery.data?.data ?? [];
  const layoutId =
    node.transformation.type === 'render' ? String(node.transformation.textLayoutId ?? '') : '';

  const promote = useMutation({
    mutationFn: () =>
      api.post<ArtworkAsset>(`/product-templates/${templateId}/promote`, {
        sourceAssetId: node.id,
        textLayoutId: layoutId,
        decision,
        notes: notes || undefined,
      }),
    onSuccess: async (res) => {
      setQa((res.meta as { printQa?: PrintQaRecord } | undefined)?.printQa ?? null);
      onDone();
    },
    onError: (e) => onError(errMsg(e)),
  });

  // Only a rendered preview can be promoted to production.
  if (node.role !== 'rendered_preview') return null;

  return (
    <div className="card">
      <h2>Promote to production</h2>
      <p className="muted">
        Runs print QA against one approved product template and creates a production asset.
      </p>
      <div className="form-row">
        <label htmlFor="pr-template">Product template</label>
        <select id="pr-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
          <option value="">Select a template…</option>
          {templates
            .filter((t) => t.status === 'approved')
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} (v{t.version})
              </option>
            ))}
        </select>
      </div>
      <div className="form-row">
        <label htmlFor="pr-decision">Decision</label>
        <select
          id="pr-decision"
          value={decision}
          onChange={(e) => setDecision(e.target.value as 'pass' | 'fail')}
        >
          <option value="pass">Pass</option>
          <option value="fail">Fail</option>
        </select>
      </div>
      <div className="form-row">
        <label htmlFor="pr-notes">Reviewer notes</label>
        <input id="pr-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <button
        type="button"
        onClick={() => promote.mutate()}
        disabled={promote.isPending || !templateId}
      >
        {promote.isPending ? 'Running QA…' : 'Run print QA & promote'}
      </button>
      {qa && <PrintQaView qa={qa} />}
    </div>
  );
}

function PrintQaView({ qa }: { qa: PrintQaRecord }) {
  const rows: [string, boolean | null][] = [
    ['Transparency', qa.transparencyPass],
    ['Dimensions', qa.dimensionsPass],
    ['Safe area', qa.safeAreaPass],
    ['Handle zones', qa.handleZonePass],
    ['Text overflow', qa.textOverflowPass],
    ['Contrast', qa.contrastPass],
  ];
  const allOk = rows.every(([, ok]) => ok !== false);
  return (
    <div className={allOk ? 'qa qa-ok' : 'qa qa-bad'}>
      <strong>Print QA — {qa.decision}</strong>
      <ul>
        {rows.map(([label, ok]) => (
          <li key={label}>
            {label}: {ok == null ? '—' : ok ? '✓' : '✗'}
          </li>
        ))}
      </ul>
    </div>
  );
}
